import background1 from '../../assets/sign-in/background-1.jpg';
import background2 from '../../assets/sign-in/background-2.jpg';
import background3 from '../../assets/sign-in/background-3.jpg';
import background4 from '../../assets/sign-in/background-4.jpg';
import background5 from '../../assets/sign-in/background-5.jpg';
import background6 from '../../assets/sign-in/background-6.jpg';
import background7 from '../../assets/sign-in/background-7.jpg';
import background8 from '../../assets/sign-in/background-8.jpg';
import background9 from '../../assets/sign-in/background-9.jpg';
import background10 from '../../assets/sign-in/background-10.jpg';

/**
 * Photos and Andy-isms for the Sign In page.
 *
 * Approved Oct 1 2026: hard-coded in each app (no shared pool or CMS), in the
 * user's local time. Photos rotate by day of week, so only the first seven
 * are shown. Andy-isms (from the "Andy-isms and Alliance-Isms" glossary,
 * Oct 2026) rotate by day of month: one per date, so the last ones only show
 * in longer months. Reorder either list to change which ones appear.
 */

interface SignInPhoto {
  src: string;
  /** Describes the photo for screen readers */
  alt: string;
}

interface SignInQuote {
  /** The saying */
  text: string;
  /** What it means, shown under the saying */
  meaning: string;
}

// Square 1080×1080 event photos, shown uncropped (layout A, inset square)
const SIGN_IN_PHOTOS: SignInPhoto[] = [
  { src: background1, alt: 'An Alliance event' },
  { src: background2, alt: 'An Alliance event' },
  { src: background3, alt: 'An Alliance event' },
  { src: background4, alt: 'An Alliance event' },
  { src: background5, alt: 'An Alliance event' },
  { src: background6, alt: 'An Alliance event' },
  { src: background7, alt: 'An Alliance event' },
  { src: background8, alt: 'An Alliance event' },
  { src: background9, alt: 'An Alliance event' },
  { src: background10, alt: 'An Alliance event' },
];

const SIGN_IN_QUOTES: SignInQuote[] = [
  {
    text: 'N247RU',
    meaning:
      'I’m in 24/7. Are you? Be all in. Success comes from consistent commitment, not occasional effort.',
  },
  {
    text: 'You owe it to them to show it to them',
    meaning:
      'You were given an opportunity that changed your life. You owe it to others to show them the same path and let them decide if it’s right for them.',
  },
  {
    text: 'The biggest promoters have the biggest business',
    meaning: 'Those who consistently promote create the most opportunities.',
  },
  {
    text: 'Trust only motion',
    meaning: 'Watch actions, not words.',
  },
  {
    text: 'Do the do',
    meaning: 'Stop talking about it. Do it.',
  },
  {
    text: 'If your players aren’t good, get new players',
    meaning:
      'Either help people improve or surround yourself with different people.',
  },
  {
    text: 'You can’t win big if you don’t bet big',
    meaning: 'Big rewards require courage and commitment.',
  },
  {
    text: 'Ready, fire, aim',
    meaning: 'Take action first and adjust along the way.',
  },
  {
    text: 'GS3',
    meaning:
      'Get ’em started, goal set, go serve. A simple formula for helping new people succeed.',
  },
  {
    text: 'You’re DM right',
    meaning:
      'You’re dang right. In agreement with your managers and leadership.',
  },
  {
    text: 'Inch by inch, anything’s a cinch',
    meaning: 'Consistent small steps create big results.',
  },
  {
    text: 'If it’s going to be, it’s up to me',
    meaning: 'Take personal responsibility for your success.',
  },
  {
    text: 'BAMFAM',
    meaning:
      'Book a meeting from a meeting. Never leave a meeting without scheduling the next meeting. Keep momentum moving forward.',
  },
  {
    text: 'Scared money don’t make money',
    meaning: 'Growth requires calculated risk-taking.',
  },
  {
    text: 'Keep it high level',
    meaning:
      'Share the most important and impactful parts of your story. Be concise, relevant, and get to the point.',
  },
  {
    text: 'You have to see it to seize it',
    meaning: 'Vision comes before achievement.',
  },
  {
    text: 'Shicky boom',
    meaning: 'An expression of excitement, energy, and momentum.',
  },
  {
    text: 'Be a river, not a pond',
    meaning:
      'Information should flow. When you’re taught something, it’s your responsibility to share it with the right people and channels. Rivers keep moving; ponds gather things and keep them to themselves.',
  },
  {
    text: 'Get up in there',
    meaning: 'Take action and get directly involved.',
  },
  {
    text: 'I’m playing chess and they’re playing checkers',
    meaning: 'Think strategically and several moves ahead.',
  },
  {
    text: 'Communication + urgency = currency',
    meaning: 'Fast, effective communication creates results.',
  },
  {
    text: 'Communication is the lubrication for financial affairs',
    meaning: 'Good communication helps business and money flow smoothly.',
  },
  {
    text: 'Success is never owned, only rented',
    meaning:
      'Success must be earned every day. You can’t stop working just because you’ve already achieved it.',
  },
  {
    text: 'Boom yow',
    meaning: 'An expression of excitement, energy, and momentum.',
  },
  {
    text: 'Do your job. Stack your money. Build a business.',
    meaning:
      'Master your role, manage your finances, and create long-term opportunity.',
  },
  {
    text: 'You don’t know how you look until you get your picture took',
    meaning: 'Self-awareness comes from feedback and observation.',
  },
  {
    text: 'Bo',
    meaning: 'A term of friendship, respect, or camaraderie.',
  },
  {
    text: 'A dog in the hunt ain’t got no fleas',
    meaning: 'People actively pursuing goals don’t have time for distractions.',
  },
  {
    text: 'Put the numbers in the box',
    meaning:
      'Don’t give me excuses. Give me results small enough to go in a box.',
  },
  {
    text: 'POMPOM',
    meaning:
      'We sell people POMPOM: Plenty of Money, Peace of Mind. Financial success creates options and reduces stress.',
  },
  {
    text: 'White sheet it out',
    meaning:
      'Get a white sheet of paper, put your thoughts on paper, draw it out, plan it out.',
  },
];

/**
 * Pick today's photo and Andy-ism, in the user's local time. Everyone sees
 * the same photo on a given weekday (0 = Sunday) and the same Andy-ism on a
 * given date (1-31).
 */
export const getSignInContent = (params: { date?: Date } = {}) => {
  const date = params.date ?? new Date();
  return {
    photo: SIGN_IN_PHOTOS[date.getDay() % SIGN_IN_PHOTOS.length],
    quote: SIGN_IN_QUOTES[(date.getDate() - 1) % SIGN_IN_QUOTES.length],
  };
};
